import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';

import { Unidades } from '../../Unidades/models/unidades'; 
import { UnidadesService } from '../../Service/unidades-service';
import { UdcsService } from '../../Service/udcs-service';
import { ToastService } from '../../Service/toast-service'; 
import { UppercaseDirective } from '../../util/uppercase'; 

@Component({
  selector: 'app-unidad-edit',
  standalone: true, 
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule, 
    MatInputModule,
    UppercaseDirective
  ],
  templateUrl: './unidad-edit.html',
  styleUrl: './unidad-edit.css',
})
export class UnidadEdit implements OnInit {

  unidadForm!: FormGroup;

  // Catálogos estáticos de control para mantener consistencia en la edición
  tiposCombustible: string[] = ['GASOLINA', 'DIÉSEL', 'HÍBRIDO', 'ELÉCTRICO', 'GAS LP'];
  tiposServicio: string[] = ['PARTICULAR', 'PÚBLICO', 'CARGA', 'PLATAFORMA/APP'];
  estatusUnidad: string[] = ['ACTIVO', 'INACTIVO', 'EN TALLER', 'BAJA'];
  coloresUnidad: string[] = [
    'BLANCO', 'NEGRO', 'GRIS', 'PLATA', 'ROJO', 'AZUL', 
    'VERDE', 'AMARILLO', 'CAFÉ', 'NARANJA', 'MORADO', 'BEIGE', 'MULTICOLOR'
  ];

  // Catálogos dinámicos para resolver códigos UDC
  marcas: any[] = [];
  submarcas: any[] = [];
  listaAnios: number[] = [];

  nombreClienteResuelto: string = 'CARGANDO PROPIETARIO...';

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<UnidadEdit>,
    @Inject(MAT_DIALOG_DATA) public data: Unidades,
    private unidadesService: UnidadesService,
    private udcsService: UdcsService, // 🌟 Inyectamos tu servicio de catálogos
    private cdr: ChangeDetectorRef,
    public toastService: ToastService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // 1. Cargamos el catálogo global de marcas primero
    this.udcsService.getMarcasAutos().subscribe({
      next: (marcasData) => {
        this.marcas = marcasData;

        // 2. Si hay datos válidos de la unidad seleccionada
        if (this.data) {
          // Cargamos las submarcas de forma inmediata basadas en la marca que ya tiene guardada el carro
          if (this.data.marca) {
            this.udcsService.getSubmarcasPorMarca(this.data.marca).subscribe({
              next: (subData) => {
                this.submarcas = subData;
                
                // Aplicamos patchValue integral una vez que los catálogos interdependientes existen en memoria
                this.unidadForm.patchValue(this.data);
                this.unidadForm.get('propietario.an8')?.setValue(this.data.propietario?.an8);
                this.cdr.detectChanges();
              }
            });
          } else {
            this.unidadForm.patchValue(this.data);
            this.unidadForm.get('propietario.an8')?.setValue(this.data.propietario?.an8);
            this.cdr.detectChanges();
          }
        }
      }
    });

    // 🌟 NUEVO: Consumimos el catálogo de clientes activos para resolver el nombre en caliente
    this.unidadesService.getClientesActivos().subscribe({
      next: (listaClientes) => {
        const idBuscado = this.data.propietario?.an8;
        // Buscamos el objeto en la lista que haga match con nuestro AN8
        const clienteEncontrado = listaClientes.find(c => c.an8 === idBuscado);
        
        if (clienteEncontrado) {
          this.nombreClienteResuelto = clienteEncontrado.name;
        } else {
          this.nombreClienteResuelto = 'CLIENTE NO LOCALIZADO';
        }
        this.cdr.detectChanges();
      },
      error: (err) => console.error('Error al resolver nombre de cliente en edición', err)
    });

    this.udcsService.getRangoAnios().subscribe({
      next: (aniosData) => {
        this.listaAnios = aniosData;
        this.cdr.detectChanges();
      },
      error: (err) => console.error('Error al precargar rango de años en edición:', err)
    });    

    this.unidadForm.markAllAsTouched(); 
    this.unidadForm.markAsDirty();
  }

  // Escuchador dinámico de marcas idéntico al de unidad-add
  onMarcaSeleccionada(event: any): void {
    const codigoMarca = event.target.value;
    const submarcaControl = this.unidadForm.get('submarca');

    this.submarcas = [];
    submarcaControl?.setValue('');

    if (codigoMarca) {
      this.udcsService.getSubmarcasPorMarca(codigoMarca).subscribe({
        next: (data) => {
          this.submarcas = data; 
          this.cdr.detectChanges();
        },
        error: (err) => console.error('Error al cargar submarcas en edición', err)
      });
    }
  }  

  private initForm(): void {
    this.unidadForm = this.fb.group({
      id: [null], // El ID autoincremental de la unidad para el WHERE del SQL

      // =========================================================================
      // REFACTORIZACIÓN RELACIONAL CORE: ENLACE AL PROPIETARIO CORPORATIVO AN8
      // =========================================================================
      propietario: this.fb.group({
        an8: [{ value: null, disabled: true }, [Validators.required]] // Deshabilitado para proteger el historial JDE
      }),

      marca: ['', Validators.required],
      submarca: ['', Validators.required],
      placas: ['', Validators.required],
      modelo: ['', Validators.required],
      aniofabricacion: ['', [Validators.required, Validators.min(1900)]],
      tipocombustible: ['', Validators.required],
      tiposervicio: ['', Validators.required],
      numerovin: [''],
      numeromotor: [''],
      fechaalta: [{ value: '', disabled: true }], // Campo de auditoría inalterable
      fechabaja: [null],
      status: ['', Validators.required],
      color: [''],
      tcirculacion: ['']
    });
  }

  // =========================================================================
  // LOGICA DE PERSISTENCIA ASÍNCRONA BLINDADA
  // =========================================================================
  onSave(): void {
    if (this.unidadForm.valid) {
      // Usamos getRawValue() para rescatar los campos protegidos deshabilitados (an8, fechaalta)
      const unidadModificada: Unidades = this.unidadForm.getRawValue();
      const idUnidad = unidadModificada.id;

      if (!idUnidad) {
        console.error('Error: No se localizó el identificador de la unidad para actualizar.');
        return;
      }

      console.log('Despachando actualización relacional para Unidad ID:', idUnidad);

      // El diálogo se cierra ESTRICTAMENTE cuando el backend responde exitosamente (HTTP 200 OK)
      this.unidadesService.updateUnidad(idUnidad, unidadModificada).subscribe({
        next: (response) => {
          console.log('Confirmación de actualización exitosa recibida del servidor:', response);
          
          // ✅ CORRECTO: Solo cerramos el diálogo y enviamos el resultado. Sin Toasts aquí.
          this.dialogRef.close(unidadModificada); 
        },
        error: (err) => {
          console.error('Error crítico al procesar la actualización del vehículo:', err);
          
          // Opcional: El de error sí se puede quedar aquí, ya que si falla el backend 
          // el modal NO se cierra y el usuario necesita ver qué falló sin perder sus cambios.
          this.toastService.showDanger(
            'Error al Actualizar', 
            err.error || 'No se pudieron guardar las modificaciones en el servidor.'
          );
        }
      });
    } else {
      this.unidadForm.markAllAsTouched();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }  
  

}